/**
 * Tooltip -- lightweight hover tooltip wrapper.
 *
 * The popup renders in a portal on document.body with fixed positioning, so
 * ancestors with overflow-hidden (e.g. recommendation cards) can never clip it.
 * Placement: centered above the trigger, clamped to the viewport edges, and
 * flipped below the trigger when there is no room above.
 *
 * `interactive`: the popup takes the pointer (for content with its own hover targets, such
 * as card names) and stays open while the pointer crosses the gap into it.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  /** Extra classes on the outer wrapper span */
  className?: string;
  /** Max width of the tooltip popup, e.g. 'max-w-xs' or 'max-w-[320px]' */
  popupWidth?: string;
  /** The popup takes the pointer and stays open while hovered (see the header). */
  interactive?: boolean;
}

const EDGE_MARGIN = 8; // px between the popup and the viewport edge
const GAP = 8; // px between the trigger and the popup
const INTERACTIVE_HIDE_DELAY = 150; // ms to cross the gap into an interactive popup

interface PopupPos {
  top: number;
  left: number;
  arrowLeft: number; // arrow x within the popup, follows the trigger when clamped
  below: boolean; // true when flipped under the trigger
}

export function Tooltip({ content, children, className = '', popupWidth = 'max-w-[260px]', interactive = false }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pos, setPos] = useState<PopupPos | null>(null);

  const cancelHide = () => {
    if (hideTimer.current !== null) {
      clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };
  const show = () => {
    cancelHide();
    setVisible(true);
  };
  const hide = () => {
    cancelHide();
    if (interactive) hideTimer.current = setTimeout(() => setVisible(false), INTERACTIVE_HIDE_DELAY);
    else setVisible(false);
  };
  useEffect(() => () => {
    if (hideTimer.current !== null) clearTimeout(hideTimer.current);
  }, []);

  useLayoutEffect(() => {
    if (!visible) {
      setPos(null);
      return;
    }
    const anchor = anchorRef.current;
    const popup = popupRef.current;
    if (!anchor || !popup) return;
    const rect = anchor.getBoundingClientRect();
    const pw = popup.offsetWidth;
    const ph = popup.offsetHeight;
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(EDGE_MARGIN, Math.min(centerX - pw / 2, window.innerWidth - pw - EDGE_MARGIN));
    const below = rect.top - ph - GAP < EDGE_MARGIN;
    const top = below ? rect.bottom + GAP : rect.top - ph - GAP;
    const arrowLeft = Math.max(10, Math.min(centerX - left, pw - 10));
    setPos({ top, left, arrowLeft, below });
  }, [visible]);

  if (!content) return <>{children}</>;

  return (
    <span
      ref={anchorRef}
      className={`relative inline-flex ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {visible &&
        createPortal(
          // An interactive popup is a portal child of the anchor: React bubbles its hover to
          // the anchor's handlers, and its clicks must not reach a link the anchor wraps.
          <div
            ref={popupRef}
            role="tooltip"
            style={{
              position: 'fixed',
              top: pos?.top ?? 0,
              left: pos?.left ?? 0,
              visibility: pos ? 'visible' : 'hidden',
            }}
            onMouseEnter={interactive ? show : undefined}
            onMouseLeave={interactive ? hide : undefined}
            onClick={interactive ? (e) => e.stopPropagation() : undefined}
            className={`z-50 ${interactive ? 'pointer-events-auto' : 'pointer-events-none'} min-w-[180px] ${popupWidth} bg-surface-container-high
                       border border-outline-variant rounded-md shadow-lg px-3 py-2 text-xs
                       text-on-surface text-left`}
          >
            {content}
            {/* Arrow: points at the trigger even when the popup is clamped at an edge */}
            <span
              className={`absolute w-0 h-0 ${pos?.below ? 'bottom-full' : 'top-full'}`}
              style={{
                left: pos ? pos.arrowLeft : '50%',
                transform: 'translateX(-50%)',
                borderLeft: '5px solid transparent',
                borderRight: '5px solid transparent',
                ...(pos?.below
                  ? { borderBottom: '5px solid var(--outline-variant)' }
                  : { borderTop: '5px solid var(--outline-variant)' }),
              }}
            />
          </div>,
          document.body,
        )}
    </span>
  );
}
